import { maskDecimal } from '@givebutter/util';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InstallmentStatus } from 'assets/js/pages/pledges/usePledgeForm';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { DateTime } from 'luxon';
import { twJoin } from 'tailwind-merge';
import { Installment, Pledge } from 'types/domain';

type InstallmentProgressItem = Omit<Installment, 'date'> & {
    date: DateTime;
    number: number;
    status: InstallmentStatus;
};

type PledgeWithInstallmentProgress = Omit<Pledge, 'installments'> & {
    installments: InstallmentProgressItem[];
};

const InstallmentProgress = ({ installments }: { installments: InstallmentProgressItem[] }) => {
    const markerClass = (item: InstallmentProgressItem) => {
        return {
            [InstallmentStatus.Active]:
                'bg-blue-500 text-white border-2 border-gray-300 before:size-2 before:rounded-full before:bg-white before:content-[""]',
            [InstallmentStatus.Late]: 'bg-gray-50 border border-gray-300',
            [InstallmentStatus.Paid]: 'bg-blue-500 text-white',
            [InstallmentStatus.Upcoming]:
                'bg-gray-50 border border-gray-300 before:size-2 before:rounded-full before:bg-gray-300 before:content-[""]',
        }[item.status];
    };

    return (
        <div>
            {installments.map(item => (
                <div
                    key={item.id}
                    className={twJoin(
                        "relative flex items-center justify-between overflow-hidden pb-5 pt-1 before:absolute before:left-3 before:top-8 before:h-10 before:w-[2px] before:-translate-x-1/2 before:rounded-full before:content-[''] last:before:content-none",
                        item.status === InstallmentStatus.Active ||
                            item.status === InstallmentStatus.Upcoming
                            ? 'before:bg-gray-300'
                            : 'before:bg-blue-500',
                    )}
                >
                    <div className="relative flex gap-3">
                        <div
                            className={twJoin(
                                'flex size-6 items-center justify-center rounded-full text-sm',
                                markerClass(item),
                            )}
                        >
                            {item.status === InstallmentStatus.Paid && (
                                <FontAwesomeIcon icon={faCheck} />
                            )}
                        </div>
                        <div className="flex flex-col">
                            <div
                                className={twJoin(
                                    `font-semibold`,
                                    item.status === InstallmentStatus.Active && 'text-blue-700',
                                )}
                            >
                                <CurrencySymbol />
                                {maskDecimal(item.amount)}
                            </div>
                            <div className="text-gray-600">
                                <span
                                    className={twJoin(
                                        item.status === InstallmentStatus.Active && 'text-blue-700',
                                    )}
                                >
                                    {item.date.toFormat('LLLL d, yyyy')}
                                </span>{' '}
                                {item.status === InstallmentStatus.Late && (
                                    <span className="inline-block rounded-full bg-red-50 px-2 py-1 text-xs text-red-700">
                                        Late
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="min-w-36 text-gray-500">
                        Installment {item.number} of {installments.length}
                    </div>
                </div>
            ))}
        </div>
    );
};

export { InstallmentProgress };
export type { InstallmentProgressItem, PledgeWithInstallmentProgress };
